import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getAllFiles, getFile } from '@/store/files/actions';
import { getPokaFiles, setPokaFilesBlockFinish, setPokaFilesBlockStart } from '@/store/lms/actions';

import { selectFiles } from '@/store/files/selectors';
import { selectLMS, selectPokaFiles } from '@/store/lms/selectors';

import { Button, Flex, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import useIsMobile from '@shared/hooks/useIsMobile';

import Utils from '@/Utils';
import PropTypes from 'prop-types';

import css from './PokaFiles.module.scss';

import { FILE_IMAGE } from '@/constants/file-image';
import { FINISHED } from '@/constants/study-plan-statuses';

const MAX_FILE_NAME = 50;
const MAX_FILE_NAME_MOBILE = 20;

const PokaFiles = ({ block, getStatistic }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('pokaFiles');
  const { description, files } = useSelector(selectPokaFiles);
  const { statistic } = useSelector(selectLMS);
  const { isLoadingGetAllFiles } = useSelector(selectFiles);
  const [isMobile] = useIsMobile(576);
  const next = statistic?.blocksStat && statistic.blocksStat.filter(item => item.status !== FINISHED).length;

  useEffect(() => {
    if (block?.isAvailableForStart) {
      dispatch(setPokaFilesBlockStart(block?.blockId));
    }

    dispatch(getPokaFiles(block?.blockId));
  }, [block?.blockId, block?.isAvailableForStart]);

  const handleFinishBlock = () => {
    dispatch(
      setPokaFilesBlockFinish(block?.blockId, () => {
        getStatistic(next <= 1);
      })
    );
  };

  const downloadBlob = (blob, filename) => {
    // Создаем URL для blob
    const url = window.URL.createObjectURL(blob);

    // Создаем временный элемент <a> для загрузки
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;

    // Добавляем элемент <a> в документ
    document.body.appendChild(a);

    // Инициируем загрузку
    a.click();

    // Удаляем элемент <a> и освобождаем URL
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // скачивание файла
  const handleDownloadFile = file => {
    dispatch(
      getFile(file?.uuid, blob => {
        downloadBlob(blob, file?.name);
      })
    );
  };
  // скачивание всех файлов
  const handleDownloadAllFiles = () => {
    dispatch(
      getAllFiles(block?.id, blob => {
        downloadBlob(blob, block?.name);
      })
    );
  };

  const fileRender = useMemo(
    () =>
      files.map(file => {
        const fileImage =
          FILE_IMAGE.find(type => type.type == file.type) ?? FILE_IMAGE.find(type => type.type === 'UNDEFINED');
        const fileSize = (file?.size / 1024 / 1024).toFixed(2);
        const fileNameLength = isMobile ? MAX_FILE_NAME_MOBILE : MAX_FILE_NAME;

        return (
          <div key={file?.uuid} className={css['PokaFiles-file']} onClick={() => handleDownloadFile(file)}>
            {fileImage && <img src={fileImage.image} alt='' />}
            <div>
              <Tooltip title={file?.name.length > fileNameLength && file?.name}>
                <span data-qa='pokaFilesFileName' className={css['PokaFiles-file-name']}>
                  {Utils.trString(file?.name, fileNameLength)}
                </span>
                <span className={css['PokaFiles-file-size']}>({fileSize}MB)</span>
              </Tooltip>
            </div>
            <DownloadOutlined />
          </div>
        );
      }),
    [files, isMobile]
  );

  return (
    <div className={css['PokaFiles-wrapper']}>
      <div className={css['PokaFiles-header']}>
        <div>
          <p className={css['PokaFiles-header-subtitle']} data-qa='pokaFilesSubtitle'>
            {block?.type?.name}
          </p>
          <p className={css['PokaFiles-header-title']} data-qa='pokaFilesTitle'>
            {block?.name}
          </p>
        </div>
        <Button
          data-qa='pokaFilesDownloadAll'
          onClick={handleDownloadAllFiles}
          loading={isLoadingGetAllFiles}
          type='primary'
          icon={<DownloadOutlined />}
        >
          {t('downloadAll')}
        </Button>
      </div>
      <p className={css['PokaFiles-description']} data-qa='pokaFilesDescription'>
        {description}
      </p>

      <Flex className={css['PokaFiles-files-wrapper']} gap={20} vertical>
        {fileRender}
      </Flex>

      <Flex align='center' justify='center'>
        {block?.status !== FINISHED && (
          <Button data-qa='pokaFilesButton' type='primary' onClick={handleFinishBlock}>
            {next > 1 ? t('next') : t('end')}
          </Button>
        )}
      </Flex>
    </div>
  );
};

PokaFiles.propTypes = {
  block: PropTypes.object,
  getStatistic: PropTypes.func,
};

export default PokaFiles;
